import * as React from 'react'
import { Link } from 'react-router-dom'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/shared/lib/utils'

/** 全站标签 SSOT — 卡片角标 / 筛选 tag / 页面操作 */
export const tagVariants = cva(
  'inline-flex shrink-0 items-center justify-center whitespace-nowrap leading-none transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        glass:
          'h-4 rounded-full border border-white/10 bg-black/45 px-1.5 text-[9px] font-normal text-zinc-300 backdrop-blur-sm',
        success:
          'h-4 rounded-full border border-emerald-500/30 bg-emerald-500/12 px-1.5 text-[9px] font-normal text-emerald-300',
        sky: 'h-4 rounded-full border border-sky-500/30 bg-sky-500/12 px-1.5 text-[9px] font-normal text-sky-300',
        amber:
          'h-4 rounded-full border border-amber-500/30 bg-amber-500/12 px-1.5 text-[9px] font-normal text-amber-300',
        rose: 'h-4 rounded-full border border-rose-500/25 bg-rose-500/10 px-1.5 text-[9px] font-normal text-rose-300',
        violet:
          'h-4 rounded-full border border-violet-500/30 bg-violet-500/12 px-1.5 text-[9px] font-normal text-violet-300',
        muted:
          'h-4 rounded-full border border-zinc-600/40 bg-zinc-800/40 px-1.5 text-[9px] font-normal text-zinc-400',
        indigo:
          'h-4 rounded-full border border-indigo-500/30 bg-indigo-500/12 px-1.5 text-[9px] font-normal text-indigo-200',
        filter:
          'h-4 cursor-pointer rounded-md px-1.5 text-[9px] font-normal text-muted-foreground hover:bg-muted/80 hover:text-foreground',
        filterActive:
          'h-4 cursor-pointer rounded-md bg-primary/15 px-1.5 text-[9px] font-normal text-foreground',
        filterChip:
          'h-4 cursor-pointer rounded-full border border-border bg-background/40 px-1.5 text-[9px] font-normal text-muted-foreground hover:bg-muted/60',
        filterChipActive:
          'h-4 cursor-pointer rounded-full border border-primary/35 bg-primary/10 px-1.5 text-[9px] font-normal text-foreground',
        action:
          'h-7 cursor-pointer rounded-full border border-border bg-card px-3 text-xs font-medium text-foreground hover:bg-muted/80',
        actionActive:
          'h-7 cursor-pointer rounded-full border border-primary/40 bg-primary/12 px-3 text-xs font-medium text-foreground',
        actionTeal:
          'h-7 cursor-pointer rounded-full border border-teal-500/40 bg-teal-600 px-3 text-xs font-semibold text-white hover:bg-teal-500',
        actionTealActive:
          'h-7 cursor-pointer rounded-full border border-teal-400/50 bg-teal-500 px-3 text-xs font-semibold text-white',
        /** SegmentedTabs 内联促销角标（如「平均更省」） */
        tabHint:
          'ml-1.5 h-auto rounded-full border border-teal-500/25 bg-teal-500/10 px-1.5 py-px text-[10px] font-medium leading-none text-teal-400/90 group-data-[state=active]/trigger:border-teal-400/35 group-data-[state=active]/trigger:bg-teal-500/15 group-data-[state=active]/trigger:text-teal-300',
      },
    },
    defaultVariants: {
      variant: 'glass',
    },
  },
)

export type TagVariant = NonNullable<VariantProps<typeof tagVariants>['variant']>

export type CatalogTagTone = Extract<
  TagVariant,
  'glass' | 'success' | 'sky' | 'amber' | 'rose' | 'violet' | 'muted' | 'indigo'
>

/** 静态角标 */
export function Tag({
  className,
  variant,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof tagVariants>) {
  return <span data-slot="tag" className={cn(tagVariants({ variant }), className)} {...props} />
}

/** SegmentedTabs 内联角标 — 须放在 TabsTrigger（group/trigger）内 */
export function TabHint({ className, ...props }: React.ComponentProps<'span'>) {
  return <Tag variant="tabHint" className={className} {...props} />
}

/** 筛选组标签文字 */
export function TagLabel({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      className={cn(
        'shrink-0 text-[9px] font-normal uppercase tracking-wide text-muted-foreground',
        className,
      )}
      {...props}
    />
  )
}

type FilterTagProps = React.ComponentProps<'button'> & {
  active?: boolean
  /** 独立圆角 chip（移动端 Sheet 触发器） */
  standalone?: boolean
}

/** 可点击筛选 tag */
export function FilterTag({
  active = false,
  standalone = false,
  className,
  type = 'button',
  ...props
}: FilterTagProps) {
  const variant = standalone
    ? active
      ? 'filterChipActive'
      : 'filterChip'
    : active
      ? 'filterActive'
      : 'filter'

  return (
    <button
      type={type}
      data-slot="filter-tag"
      className={cn(tagVariants({ variant }), className)}
      {...props}
    />
  )
}

type FilterTagGroupProps = {
  label?: React.ReactNode
  children: React.ReactNode
  className?: string
  labelClassName?: string
}

/** 分段筛选容器 */
export function FilterTagGroup({ label, children, className, labelClassName }: FilterTagGroupProps) {
  return (
    <div className={cn('flex min-w-0 items-center gap-1.5', className)}>
      {label ? <TagLabel className={labelClassName}>{label}</TagLabel> : null}
      <div
        role="group"
        className="inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-md border border-border bg-muted/25 p-0.5"
      >
        {children}
      </div>
    </div>
  )
}

type ActionTagLinkProps = {
  to: string
  children: React.ReactNode
  active?: boolean
  emphasis?: 'default' | 'teal' | 'sky'
  className?: string
}

/** 页面标题区操作链接 tag */
export function ActionTagLink({
  to,
  children,
  active = false,
  emphasis = 'default',
  className,
}: ActionTagLinkProps) {
  const variant =
    emphasis === 'teal'
      ? active
        ? 'actionTealActive'
        : 'actionTeal'
      : active
        ? 'actionActive'
        : 'action'

  return (
    <Link to={to} data-slot="action-tag" className={cn(tagVariants({ variant }), className)}>
      {children}
    </Link>
  )
}
